/**
 * "What you had on Pro Plus" — the value half of the lapsed paywall.
 *
 * Icon treatment borrowed from `dialogs/DowngradeComparisonDialog` (glyph in a muted circle)
 * because the two moments are the same argument seen from opposite sides: that dialog shows
 * what you LOSE by going down, this one shows what you already had. Reusing the visual
 * language means a user who has seen one recognises the other.
 *
 * Not a card and not a comparison grid, unlike that dialog: there is no second plan to
 * compare against and no trailing check column, since every row is included by definition.
 * A bordered box here would read as a third panel inside a dialog that already has two.
 *
 * Labels are the same i18n keys the plan cards use (`wizard/plan-feature-copy.ts`), so the
 * copy a user reads here matches the Pro Plus card they land on after "Subscribe".
 * Deliberately NOT imported from that map: this list is trimmed and ordered for a narrow
 * column, and the card grid's list changing should not silently reflow this one.
 */
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  Building2,
  KeyRound,
  Link2,
  RefreshCw,
  Rocket,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

interface FeatureRow {
  icon: LucideIcon;
  /** Display text AND i18n key (project convention). */
  labelKey: string;
}

const ROWS: readonly FeatureRow[] = [
  { icon: Rocket, labelKey: '100+ Apps & Integrations' },
  { icon: Link2, labelKey: 'Microsoft 365 & Google Workspace Integration' },
  { icon: BarChart3, labelKey: 'Display Dashboards & Websites' },
  { icon: RefreshCw, labelKey: 'OptiSync - Dynamic Data Mapping' },
  { icon: Wrench, labelKey: 'Remote Troubleshooting' },
  { icon: Building2, labelKey: 'Unlimited Users, Teams, Roles, & Audit Log' },
  { icon: KeyRound, labelKey: 'Single Sign-On with SAML' },
];

export function LapsedProPlusFeatures() {
  const { t } = useTranslation();

  return (
    <div className="space-y-3" data-testid="lapsed-proplus-features">
      <p className="text-base font-semibold text-foreground">{t('What you had on Pro Plus')}</p>
      {/* space-y-3, not space-y-1: at 4px the rows read as one dense block, and the icon
          chips (h-8 circles) made the gaps look tighter still. This is a list someone is meant
          to read line by line, so each row needs to be its own line. */}
      <ul className="space-y-3">
        {ROWS.map((row) => {
          const Icon = row.icon;
          return (
            <li key={row.labelKey} className="flex items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
                <Icon className="h-4 w-4 text-foreground" aria-hidden="true" />
              </span>
              <span className="text-sm text-foreground">{t(row.labelKey)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
